import { useForm } from "react-hook-form"
import { Link, useNavigate, useSearchParams } from "react-router"
import { api, ApiError } from "../../lib/api"
import { useAuthRedirect } from "../../hooks/useAuthRedirect"
import OAuthButtons from "../../components/OAuthButtons"
import appName from "../../lib/appName"
import "../../styles/auth.scss"

type FormValues = {
  name: string
  email: string
  password: string
  password_confirmation: string
}

export default function SignupPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const redirect = searchParams.get("redirect")
  const authStatus = useAuthRedirect(redirect || "/")

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>()

  const onSubmit = async (data: FormValues) => {
    try {
      await api.post("/users", { user: data })
      navigate(redirect || "/onboarding")
    } catch (err) {
      const { data: errData } = err as ApiError
      const messages = errData.errors ?? ["エラーが発生しました"]
      setError("root", { message: messages.join(", ") })
    }
  }

  if (authStatus === "loading") return null

  return (
    <div className="auth-layout">
      <div className="auth-card">
        <div className="auth-logo">
          <img src="/logo.png" alt={appName()} className="auth-logo__img" />
        </div>

        <h1 className="auth-title">アカウントを作成</h1>
        <p className="auth-subtitle">無料ではじめましょう</p>

        <OAuthButtons action="登録" redirect={redirect} />
        <p className="auth-oauth-notice">
          <i className="bi bi-info-circle" />
          Googleで登録した場合、メールアドレスの変更はできません
        </p>

        <div className="divider"><span>または</span></div>

        {errors.root && (
          <div className="error-banner">{errors.root.message}</div>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="form-group">
            <label>名前</label>
            <input
              type="text"
              placeholder="山田 太郎"
              className={errors.name ? "error" : ""}
              {...register("name", { required: "名前を入力してください" })}
            />
            {errors.name && <p className="field-error">{errors.name.message}</p>}
          </div>

          <div className="form-group">
            <label>メールアドレス</label>
            <input
              type="email"
              placeholder="example@company.com"
              className={errors.email ? "error" : ""}
              {...register("email", { required: "メールアドレスを入力してください" })}
            />
            {errors.email && <p className="field-error">{errors.email.message}</p>}
          </div>

          <div className="form-group">
            <label>パスワード</label>
            <input
              type="password"
              placeholder="8文字以上"
              className={errors.password ? "error" : ""}
              {...register("password", {
                required: "パスワードを入力してください",
                minLength: { value: 8, message: "8文字以上で入力してください" },
              })}
            />
            {errors.password && <p className="field-error">{errors.password.message}</p>}
          </div>

          <div className="form-group">
            <label>パスワード（確認）</label>
            <input
              type="password"
              placeholder="パスワードを再入力"
              className={errors.password_confirmation ? "error" : ""}
              {...register("password_confirmation", { required: "パスワード確認を入力してください" })}
            />
            {errors.password_confirmation && (
              <p className="field-error">{errors.password_confirmation.message}</p>
            )}
          </div>

          <button type="submit" className="btn-primary" disabled={isSubmitting}>
            {isSubmitting ? "処理中..." : "アカウントを作成"}
          </button>
        </form>

        <div className="auth-footer">
          すでにアカウントをお持ちですか？ <Link to={redirect ? `/login?redirect=${encodeURIComponent(redirect)}` : "/login"}>ログイン</Link>
        </div>
        <div className="auth-footer auth-footer--legal">
          <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer">プライバシーポリシー</a>
          <span>・</span>
          <a href="/terms.html" target="_blank" rel="noopener noreferrer">利用規約</a>
          <span>・</span>
          <a href="/transactions_law.html" target="_blank" rel="noopener noreferrer">特定商取引法に基づく表記</a>
        </div>
      </div>
    </div>
  )
}
